import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { portalApi, portalError } from "./portalApi";
import { Btn, Label, Select, Modal, Badge, Empty } from "../components/UI";
import { Star, MessageSquareHeart, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export function Stars({ value, onChange, testId }) {
  return (
    <div className="flex gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`${testId}-${n}`} onClick={() => onChange(n)}
          className="p-0.5 transition-transform hover:scale-110">
          <Star size={26} className={n <= value ? "fill-[#FFB300] text-[#FFB300]" : "text-[#D7CCC8]"} />
        </button>
      ))}
    </div>
  );
}

const SURVEY_EMPTY = { overall: 0, provider_rating: 0, facility_rating: 0, recommend: 8, comments: "" };

export default function PortalFeedback() {
  const [pending, setPending] = useState([]);
  const [mySurveys, setMySurveys] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [surveyFor, setSurveyFor] = useState(null);
  const [survey, setSurvey] = useState(SURVEY_EMPTY);
  const [fb, setFb] = useState({ category: "General", rating: 0, message: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    portalApi.get("/portal/surveys/pending").then((r) => setPending(r.data));
    portalApi.get("/portal/surveys").then((r) => setMySurveys(r.data));
    portalApi.get("/portal/feedback").then((r) => setFeedback(r.data));
  };
  useEffect(() => { load(); }, []);

  const submitSurvey = async (e) => {
    e.preventDefault();
    if (!survey.overall || !survey.provider_rating || !survey.facility_rating) return toast.error("Please rate all three areas");
    setBusy(true);
    try {
      await portalApi.post("/portal/surveys", { appointment_id: surveyFor.id, ...survey });
      toast.success("Thank you! Your feedback helps us improve your care.");
      setSurveyFor(null); setSurvey(SURVEY_EMPTY); load();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/feedback", fb);
      toast.success("Feedback sent to the clinic - thank you!");
      setFb({ category: "General", rating: 0, message: "" }); load();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="portal-feedback-page">
      <div>
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">Feedback & Surveys</h1>
        <p className="text-sm text-[#7B6157]">Rate your visits and tell us how we're doing - your voice shapes your care.</p>
      </div>

      {pending.length > 0 && (
        <div className="rounded-2xl border border-[#FFB300] bg-[#FFF8E1] p-4" data-testid="portal-pending-surveys">
          <div className="flex items-center gap-2 text-sm font-bold text-[#8D6E00] mb-2"><ClipboardCheck size={15} /> Rate your recent visit{pending.length > 1 ? "s" : ""}</div>
          <div className="space-y-2">
            {pending.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white px-4 py-2.5">
                <span className="text-sm font-semibold text-[#3E2723]">{a.type}</span>
                <span className="text-xs text-[#7B6157]">{dayjs(a.date).format("MMM D, YYYY")} · {a.start_time}</span>
                <Btn data-testid={`portal-survey-btn-${a.id}`} className="ml-auto px-3.5 py-1.5 text-xs" onClick={() => { setSurvey(SURVEY_EMPTY); setSurveyFor(a); }}>Take 30-second Survey</Btn>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submitFeedback} className="rounded-2xl bg-white border border-[#EFEBE0] p-5" data-testid="portal-feedback-form">
        <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723] mb-3"><MessageSquareHeart size={15} className="text-[#7B6157]" /> Send Feedback to the Clinic</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Topic</Label>
            <Select data-testid="portal-fb-category" value={fb.category} onChange={(e) => setFb({ ...fb, category: e.target.value })}>
              {["General", "My Care", "Scheduling", "Billing", "Facility", "Portal / Technology", "Compliment", "Complaint"].map((c) => <option key={c}>{c}</option>)}
            </Select></div>
          <div><Label>Overall rating (optional)</Label><Stars value={fb.rating} onChange={(rating) => setFb({ ...fb, rating })} testId="portal-fb-stars" /></div>
        </div>
        <div className="mt-4"><Label>Your feedback</Label>
          <textarea data-testid="portal-fb-message" value={fb.message} onChange={(e) => setFb({ ...fb, message: e.target.value })} required rows={3}
            className="w-full rounded-xl border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] focus:outline-none focus:ring-2 focus:ring-[#8D6E63]"
            placeholder="Tell us what's working or what we can improve…" /></div>
        <div className="mt-3 flex justify-end"><Btn data-testid="portal-fb-submit" type="submit" disabled={busy}>{busy ? "Sending…" : "Send Feedback"}</Btn></div>
      </form>

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">My Visit Surveys</h3>
          {mySurveys.length === 0 ? <Empty text="No surveys yet" /> : (
            <div className="space-y-2">
              {mySurveys.map((s) => (
                <div key={s.id} className="rounded-xl bg-white border border-[#EFEBE0] px-4 py-3" data-testid={`portal-survey-${s.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-[#3E2723]">{s.appointment_type}</span>
                    <span className="text-xs text-[#7B6157]">{dayjs(s.appointment_date).format("MMM D")}</span>
                    <span className="ml-auto flex items-center gap-0.5 font-bold text-[#8D5A00]">{s.overall} <Star size={13} className="fill-[#FFB300] text-[#FFB300]" /></span>
                  </div>
                  {s.comments && <div className="mt-1 text-xs text-[#7B6157]">"{s.comments}"</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">My Feedback History</h3>
          {feedback.length === 0 ? <Empty text="No feedback sent yet" /> : (
            <div className="space-y-2">
              {feedback.map((f) => (
                <div key={f.id} className="rounded-xl bg-white border border-[#EFEBE0] px-4 py-3" data-testid={`portal-fb-${f.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-[#3E2723]">{f.category}</span>
                    <span className="text-xs text-[#84675B]">{dayjs(f.created_at).format("MMM D")}</span>
                    <span className="ml-auto"><Badge value={f.status === "reviewed" ? "completed" : "open"} /></span>
                  </div>
                  <div className="mt-1 text-xs text-[#7B6157] line-clamp-2">{f.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal open={!!surveyFor} onClose={() => setSurveyFor(null)} title={surveyFor ? `How was your ${surveyFor.type}?` : ""}>
        {surveyFor && (
          <form onSubmit={submitSurvey} className="space-y-4">
            <p className="text-xs text-[#84675B]">{dayjs(surveyFor.date).format("dddd, MMMM D")} at {surveyFor.start_time}</p>
            <div><Label>Overall experience</Label><Stars value={survey.overall} onChange={(overall) => setSurvey({ ...survey, overall })} testId="survey-overall" /></div>
            <div><Label>Your provider</Label><Stars value={survey.provider_rating} onChange={(provider_rating) => setSurvey({ ...survey, provider_rating })} testId="survey-provider" /></div>
            <div><Label>Facility & front desk</Label><Stars value={survey.facility_rating} onChange={(facility_rating) => setSurvey({ ...survey, facility_rating })} testId="survey-facility" /></div>
            <div>
              <Label>How likely are you to recommend us? ({survey.recommend}/10)</Label>
              <input data-testid="survey-recommend" type="range" min="0" max="10" value={survey.recommend}
                onChange={(e) => setSurvey({ ...survey, recommend: Number(e.target.value) })} className="w-full accent-[#4E342E]" />
              <div className="flex justify-between text-[10px] text-[#84675B]"><span>Not likely</span><span>Extremely likely</span></div>
            </div>
            <div><Label>Comments (optional)</Label>
              <textarea data-testid="survey-comments" value={survey.comments} onChange={(e) => setSurvey({ ...survey, comments: e.target.value })} rows={2}
                className="w-full rounded-xl border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" /></div>
            <div className="flex justify-end gap-2">
              <Btn type="button" variant="ghost" onClick={() => setSurveyFor(null)}>Later</Btn>
              <Btn data-testid="survey-submit" type="submit" disabled={busy}>{busy ? "Submitting…" : "Submit Survey"}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
